import React from 'react';
import { Star, AlertTriangle } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { localize } from '../../data/i18nData';

export function LoanProposalCard({ proposal, bank: rawBank, durationMonths, onAccept }) {
  const bank = localize('bank', rawBank);
  if (!proposal.eligible) {
    return (
      <div className="rounded-lg border border-danger/40 bg-danger/5 p-4" data-testid={`proposal-${proposal.bankId}`}>
        <div className="flex items-center gap-3 mb-2">
          <img src={bank.advisor.portrait} alt={bank.advisor.name} className="w-10 h-10 rounded-full object-cover object-top border-2" style={{ borderColor: bank.color }} />
          <div>
            <p className="text-sm font-bold" style={{ color: bank.color }}>{bank.name}</p>
            <p className="text-[10px] text-slate-500">{bank.advisor.name}</p>
          </div>
        </div>
        <p className="text-xs text-danger italic">"{bank.advisor.loanRefusal[0]}"</p>
        <p className="text-[10px] text-danger mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {proposal.reason}</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border-2 p-4 transition-all " style={{ borderColor: bank.accentColor + '60' }} data-testid={`proposal-${proposal.bankId}`}>
      <div className="flex items-center gap-3 mb-3">
        <img src={bank.advisor.portrait} alt={bank.advisor.name} className="w-10 h-10 rounded-full object-cover object-top border-2" style={{ borderColor: bank.color }} />
        <div className="flex-1">
          <p className="text-sm font-bold" style={{ color: bank.color }}>{bank.name}</p>
          <p className="text-[10px] text-slate-500">{bank.advisor.name}</p>
        </div>
        {proposal.loyaltyLevel >= 2 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-warn/15 text-warn font-medium flex items-center gap-0.5">
            <Star className="w-2.5 h-2.5 fill-amber-500" /> Taux fidélité
          </span>
        )}
      </div>

      <p className="text-xs text-slate-400 italic mb-3">"{proposal.approvalMessage}"</p>

      {/* Detailed proposal info */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-ink-700/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-slate-500">Taux annuel</p>
          <p className="text-lg font-bold" style={{ color: bank.color }}>{proposal.ratePercent}%</p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-slate-500">Mensualité</p>
          <p className="text-lg font-bold text-slate-200">{formatMoney(proposal.monthly)}</p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-slate-500">Durée</p>
          <p className="text-lg font-bold text-foreground">{durationMonths} mois</p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-slate-500">Montant accordé</p>
          <p className="text-sm font-bold text-slate-300">{formatMoney(proposal.amount)}</p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-slate-500">Coût total</p>
          <p className="text-sm font-bold text-slate-300">{formatMoney(proposal.totalCost)}</p>
        </div>
        <div className="bg-ink-700/30 rounded-lg p-2 text-center">
          <p className="text-[10px] text-slate-500">Intérêts totaux</p>
          <p className="text-sm font-bold text-danger">{formatMoney(proposal.totalInterest)}</p>
        </div>
      </div>

      {/* Capacity detail */}
      {proposal.capacityDetail && (
        <div className="bg-accent/10 rounded-lg p-2 mb-3 text-[10px] text-accent space-y-0.5" data-testid={`capacity-detail-${proposal.bankId}`}>
          <p className="font-semibold">Détail de la capacité d'emprunt :</p>
          <p>Plafond d'endettement : {formatMoney(proposal.capacityDetail.maxDebt)} ({proposal.capacityDetail.ratioLabel})</p>
          <p>Dette actuelle : {formatMoney(proposal.capacityDetail.currentDebt)}</p>
          <p>Capacité restante : {formatMoney(proposal.capacityDetail.remaining)}</p>
          {proposal.amount < proposal.capacityDetail.requestedAmount && (
            <p className="text-warn font-medium">Montant réduit de {formatMoney(proposal.capacityDetail.requestedAmount)} à {formatMoney(proposal.amount)} (capacité insuffisante)</p>
          )}
        </div>
      )}

      <button
        onClick={() => onAccept(proposal.bankId)}
        className="w-full py-2.5 rounded-lg text-sm font-bold text-foreground transition-colors hover:opacity-90"
        style={{ backgroundColor: bank.color }}
        data-testid={`accept-loan-${proposal.bankId}`}
      >
        Accepter cette offre
      </button>
    </div>
  );
}
